import { Pencil } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { cn } from "@/lib/utils";
import { richTextHtml } from "@/lib/rich-text";
import { statCardSurface } from "@/components/common/statCardSurface";
import { Button } from "@/components/ui/button";
import type { Recipe } from "@/types/recipe";

interface GeneralDetailsTabProps {
  recipe: Recipe;
  onEdit?: () => void;
}

export function GeneralDetailsTab({ recipe, onEdit }: GeneralDetailsTabProps) {
  const { t } = useLingui();

  return (
    <div className="flex flex-col items-start w-full">
      <div className="flex flex-col gap-6 px-6 w-full">
        {/* Section header */}
        <div className="flex flex-col gap-5 pb-px">
          <div className="flex gap-4 items-start">
            <div className="flex-1 flex flex-col gap-1 min-w-0">
              <p className="text-2xl font-medium leading-[28px] text-(--color-text-primary)">
                {t`General Details`}
              </p>
              <p className="text-sm text-(--color-text-secondary)">
                {t`Basic information about this recipe.`}
              </p>
            </div>
            {onEdit && (
              <Button variant="brand-outline" onClick={onEdit} className="shrink-0">
                <Pencil className="size-4" />
                {t`Edit`}
              </Button>
            )}
          </div>
          <div className="h-px bg-(--color-surface-muted)" />
        </div>

        {/* Form rows */}
        <div className="flex flex-col gap-5">
          <FormRow label={t`Name`}>
            <p className="text-base leading-[24px] text-(--color-text-primary)">{recipe.name}</p>
          </FormRow>
          <div className="h-px bg-(--color-surface-muted)" />

          <FormRow label={t`Status`}>
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "size-3 rounded-full shrink-0",
                  recipe.isPublished ? "bg-(--brand-blue)" : "bg-(--color-surface-accent)",
                )}
              />
              <p className="text-base font-medium leading-[24px] text-(--color-text-primary)">
                {recipe.isPublished ? t`Published` : t`Draft`}
              </p>
            </div>
          </FormRow>
          <div className="h-px bg-(--color-surface-muted)" />

          <FormRow
            label={t`Recipe profile photo`}
            sublabel={t`Profile image for this recipe.`}
          >
            {recipe.imageUrl ? (
              <div className="size-16 rounded-[12px] overflow-hidden border border-(--gray-50) shrink-0">
                <img src={recipe.imageUrl} alt="" className="size-full object-cover" />
              </div>
            ) : (
              <div className="size-16 rounded-[12px] bg-(--color-surface-muted) border border-(--gray-50) shrink-0" />
            )}
          </FormRow>
          <div className="h-px bg-(--color-surface-muted)" />

          <FormRow
            label={t`Background photo`}
            sublabel={t`Background image displayed in recipe header.`}
          >
            {recipe.imageUrl ? (
              <div className="w-full max-w-[600px] h-[126px] rounded-[10px] overflow-hidden border border-(--gray-50)">
                <img src={recipe.imageUrl} alt="" className="size-full object-cover" />
              </div>
            ) : (
              <div className="w-full max-w-[600px] h-[126px] rounded-[10px] bg-(--color-surface-muted) border border-(--gray-50)" />
            )}
          </FormRow>
          <div className="h-px bg-(--color-surface-muted)" />

          <FormRow
            label={t`Short dish description`}
            sublabel={t`Write a short introduction.`}
          >
            {recipe.description ? (
              <div
                className="text-base leading-[24px] text-(--color-text-primary) w-full max-w-[600px] [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
                dangerouslySetInnerHTML={{ __html: richTextHtml(recipe.description) }}
              />
            ) : (
              <p className="text-base leading-[24px] text-(--color-text-primary) w-full max-w-[600px]">—</p>
            )}
          </FormRow>
          <div className="h-px bg-(--color-surface-muted)" />

          <FormRow
            label={t`Yield & Portioning`}
            sublabel={t`Defines scaling, cost-per-portion, and batch planning.`}
          >
            <div className="flex flex-col gap-3 w-full max-w-[600px]">
              <StatCard
                label={t`Total Yield`}
                value={recipe.yieldQuantity?.toString() ?? "—"}
                unit={recipe.yieldUom ?? ""}
              />
              <StatCard
                label={t`Portion Size`}
                value={recipe.portionQuantity?.toString() ?? "—"}
                unit={recipe.portionUom ?? ""}
              />
              <StatCard
                label={t`Portions per Batch`}
                value={recipe.servings?.toString() ?? "—"}
                unit={t`Calculated (Yield ÷ Portion size)`}
              />
            </div>
          </FormRow>
        </div>
      </div>
    </div>
  );
}

function FormRow({
  label,
  sublabel,
  children,
}: {
  label: string;
  sublabel?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-8 items-start">
      <div className="flex flex-col gap-0 w-[340px] shrink-0">
        <p className="text-sm font-medium leading-[20px] text-(--color-text-primary)">{label}</p>
        {sublabel && (
          <p className="text-sm text-(--color-text-secondary) leading-[20px]">{sublabel}</p>
        )}
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

function StatCard({
  label,
  value,
  unit,
}: {
  label: string;
  value: string;
  unit: string;
}) {
  return (
    <div className={cn(statCardSurface, "p-3 flex flex-col gap-2 w-full")}>
      <p className="text-sm text-(--color-text-secondary) leading-[1.4]">{label}</p>
      <div className="flex flex-col gap-1">
        <p className="text-xl font-medium leading-none text-(--color-text-primary)">{value}</p>
        {unit && <p className="text-xs text-(--color-text-secondary) leading-[1.4]">{unit}</p>}
      </div>
    </div>
  );
}
